import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  FaTachometerAlt,
  FaWallet,
  FaUser,
  FaShoppingBag,
  FaMapMarkerAlt,
  FaHeart,
  FaCreditCard,
  FaPencilAlt,
  FaTrash,
  FaPlus,
  FaMinus,
  FaUndo,
  FaTimes,
  FaCheckCircle,
  FaCheck,
  FaBoxOpen,
  FaCamera,
  FaExclamationCircle,
  FaEllipsisV,
  FaShoppingCart,
  FaHeadset,
  FaClock,
  FaTruck,
  FaExclamationTriangle,
  FaEnvelope,
  FaBell,
  FaBellSlash,
  FaBars,
} from "react-icons/fa";
import {
  // ... other imports
  FaLayerGroup,
} from "react-icons/fa";

// ─── Returns Section ─────────────────────────────────────────────
const ReturnsSection = ({ orders }) => {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(false);

  const fetchReturns = async () => {
    setLoadingReturns(true);
    const token = localStorage.getItem("authToken");
    let customerId = null;
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const parsed = JSON.parse(userData);
        customerId = parsed.id || parsed.customer_id || null;
      }
    } catch (e) {}
    if (!token || !customerId) {
      setLoadingReturns(false);
      return;
    }
    try {
      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/customer/returns/?customer_id=${customerId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setReturns(
          data.success && Array.isArray(data.returns) ? data.returns : [],
        );
      }
    } catch (err) {
      console.error("Error fetching returns:", err);
    } finally {
      setLoadingReturns(false);
    }
  };

  useEffect(() => {
    fetchReturns();
  }, []);

  const getStatusConfig = (status) => {
    const s = status?.toLowerCase() || "";
    if (s === "requested" || s === "pending")
      return {
        bg: "#fff3cd",
        color: "#856404",
        icon: <FaClock />,
        label: "Requested",
      };
    if (s === "approved")
      return {
        bg: "#d4edda",
        color: "#155724",
        icon: <FaCheckCircle />,
        label: "Approved",
      };
    if (s === "processing")
      return {
        bg: "#cce5ff",
        color: "#004085",
        icon: <FaTruck />,
        label: "Processing",
      };
    if (s === "completed")
      return {
        bg: "#d4edda",
        color: "#155724",
        icon: <FaCheck />,
        label: "Completed",
      };
    if (s === "rejected" || s === "cancelled")
      return {
        bg: "#f8d7da",
        color: "#721c24",
        icon: <FaExclamationTriangle />,
        label: "Rejected",
      };
    return {
      bg: "#e2e3e5",
      color: "#383d41",
      icon: <FaBoxOpen />,
      label: status,
    };
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "N/A";

  return (
    <div className="dashboard-card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "20px",
        }}
      >
        <div className="title" style={{ marginBottom: "0" }}>
          <h2 style={{ fontSize: "22px", margin: 0 }}>My Returns</h2>
          <span className="title-leaf">
            <svg className="icon-width bg-gray">
              <use href="../assets/svg/leaf.svg#leaf"></use>
            </svg>
          </span>
        </div>
      </div>
      {loadingReturns ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : returns.length > 0 ? (
        <div className="row g-4">
          {returns.map((returnItem) => {
            const sc = getStatusConfig(returnItem.status);
            return (
              <div className="col-lg-6 col-xl-4" key={returnItem.return_id}>
                <div
                  style={{
                    background: "#fff",
                    borderRadius: "12px",
                    padding: "16px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                    height: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <span style={{ fontSize: "12px", color: "#888" }}>
                        Return ID
                      </span>
                      <h6
                        style={{
                          margin: "2px 0 0",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        {returnItem.return_id}
                      </h6>
                    </div>
                    <span
                      style={{
                        background: sc.bg,
                        color: sc.color,
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "11px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {sc.icon} {sc.label}
                    </span>
                  </div>

                  <div style={{ marginBottom: "12px" }}>
                    <span style={{ fontSize: "12px", color: "#888" }}>
                      Order ID
                    </span>
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "13px",
                        fontWeight: "500",
                      }}
                    >
                      {returnItem.order_id}
                    </p>
                  </div>
                  <div style={{ marginBottom: "12px" }}>
                    <span style={{ fontSize: "12px", color: "#888" }}>
                      Rma Number
                    </span>
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: "13px",
                        fontWeight: "500",
                      }}
                    >
                      {returnItem.rma_number}
                    </p>
                  </div>
                  <div style={{ marginBottom: "12px" }}>
                    <span style={{ fontSize: "12px", color: "#888" }}>
                      Refund Amount
                    </span>
                    <h5
                      style={{
                        margin: "2px 0 0",
                        color: "#0f3460",
                        fontWeight: "700",
                      }}
                    >
                      ₹{parseFloat(returnItem.refund_amount || 0).toFixed(2)}
                    </h5>
                  </div>
                  <div style={{ marginBottom: "12px" }}>
                    <span style={{ fontSize: "12px", color: "#888" }}>
                      Reason
                    </span>
                    <p style={{ margin: "2px 0 0", fontSize: "13px" }}>
                      {returnItem.return_reason}
                    </p>
                  </div>
                  {returnItem.items?.length > 0 && (
                    <div style={{ marginBottom: "12px" }}>
                      <span style={{ fontSize: "12px", color: "#888" }}>
                        Items
                      </span>
                      <div style={{ marginTop: "4px" }}>
                        {returnItem.items.map((item, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: "12px",
                              padding: "2px 0",
                            }}
                          >
                            <span>
                              {item.product_name} x{item.quantity}
                            </span>
                            <span style={{ fontWeight: "500" }}>
                              ₹{parseFloat(item.price).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#888",
                      borderTop: "1px solid #f0f0f0",
                      paddingTop: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <div>
                      <strong>Requested:</strong>{" "}
                      {formatDate(returnItem.requested_at)}
                    </div>
                    {returnItem.pickup_date && (
                      <div style={{ marginTop: "4px" }}>
                        <strong>Pickup:</strong>{" "}
                        {formatDate(returnItem.pickup_date)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            background: "#f8f9fa",
            borderRadius: "12px",
          }}
        >
          <div style={{ marginBottom: "16px", opacity: 0.45 }}>
            <FaUndo size={48} color="#999" />
          </div>
          <h4 style={{ color: "#333", fontWeight: "500", marginBottom: "8px" }}>
            No returns found
          </h4>
          <p style={{ color: "#888", fontSize: "14px" }}>
            You haven't requested any returns yet.
          </p>
        </div>
      )}
    </div>
  );
};

// ── Helpers ───────────────────────────────────────────────────────
const validateEmail = (email) => {
  const trimmedEmail = (email || "").trim();

  // Basic check
  if (!trimmedEmail) return false;

  // Strict email regex - requires proper format
  const strictEmailRegex =
    /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;

  // Additional checks
  if (!strictEmailRegex.test(trimmedEmail)) return false;

  // Must have @ symbol
  if (trimmedEmail.indexOf("@") === -1) return false;

  // Must have domain with dot
  const domain = trimmedEmail.split("@")[1];
  if (!domain || domain.indexOf(".") === -1) return false;

  // No spaces allowed
  if (trimmedEmail.includes(" ")) return false;

  // Valid domain extension (at least 2 chars)
  const domainParts = domain.split(".");
  const tld = domainParts[domainParts.length - 1];
  if (tld.length < 2 || tld.length > 6) return false;

  return true;
};

const validatePhone = (phone) =>
  /^[6-9]\d{9}$/.test((phone || "").replace(/\D/g, ""));

const getAuthHeaders = (token, json = true) => ({
  Authorization: `Bearer ${token}`,
  ...(json ? { "Content-Type": "application/json" } : {}),
});

const safeJson = async (response) => {
  const ct = response.headers.get("content-type") || "";
  if (ct.includes("application/json")) return response.json();
  await response.text();
  return {};
};

// ─────────────────────────────────────────────────────────────────

const Account = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(
    () => localStorage.getItem("newsletterSubscribed") === "true",
  );
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  // Add this near your other useState declarations
  const [newsletterError, setNewsletterError] = useState("");

  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    date_of_birth: "",
    first_name: "",
    last_name: "",
  });
  const [addresses, setAddresses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [billingAddress, setBillingAddress] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);

  const [currentItem, setCurrentItem] = useState(null);
  const [activeSection, setActiveSection] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);

  const getImageUrl = (path) => {
    if (!path) return "/assets/images/Yeast_Protein.png";
    return path.startsWith("http")
      ? path
      : `https://evercorenutrition.com${path}`;
  };

  const silentRefresh = useCallback(async () => {
    try {
      await Promise.all([
        fetchProfileData(true),
        fetchWishlist(),
        fetchOrders(),
      ]);
    } catch (err) {
      console.error("silentRefresh error:", err);
    }
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem("authToken");
      let customerId = null;
      try {
        const ud = localStorage.getItem("userData");
        if (ud) {
          const p = JSON.parse(ud);
          customerId = p.id || p.customer_id || null;
        }
      } catch (e) {}

      if (!token || !customerId) return;

      const res = await fetch(
        `https://evercorenutrition.com/ev_admin/api/orders/?customer_id=${customerId}`,
        { method: "GET", headers: getAuthHeaders(token) },
      );

      if (res.ok) {
        const data = await res.json();
        console.log("Orders API response:", data);

        // Handle the response structure from your JSON
        let ordersList = [];

        // Check if response has success flag and orders array (like your JSON)
        if (data.success === true && Array.isArray(data.orders)) {
          ordersList = data.orders;
        }
        // Check if data is an array (direct list)
        else if (Array.isArray(data)) {
          ordersList = data;
        }
        // Check if data has results property (paginated)
        else if (data.results && Array.isArray(data.results)) {
          ordersList = data.results;
        }
        // Check if data has data property
        else if (data.data && Array.isArray(data.data)) {
          ordersList = data.data;
        }

        console.log("Processed orders list:", ordersList);
        console.log("Total orders from API:", data.total_orders);

        setOrders(ordersList);

        // Filter pending orders - check various possible status values
        const pending = ordersList.filter((o) => {
          const status = (o.status || "").toLowerCase();
          const statusDisplay = (o.status_display || "").toLowerCase();
          return (
            status === "pending" ||
            statusDisplay === "pending" ||
            status === "processing" ||
            statusDisplay === "processing" ||
            status.includes("pending")
          );
        });

        setPendingOrders(pending);
        console.log("Pending orders count:", pending.length);
      } else {
        console.error("Failed to fetch orders:", res.status);
      }
    } catch (err) {
      console.error("fetchOrders error:", err);
    }
  };

  const handleTrackOrder = (orderId) => {
    let customerId = null;
    try {
      const ud = localStorage.getItem("userData");
      if (ud) {
        const p = JSON.parse(ud);
        customerId = p.id || p.customer_id || null;
      }
    } catch (e) {}
    navigate(`/track-order/${orderId}`, {
      state: { customerId, fromOrders: true },
    });
  };

  useEffect(() => {
    fetchProfileData();
    fetchWishlist();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const m = window.innerWidth <= 768;
      setIsMobile(m);
      if (!m) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchProfileData = async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    setError(null);
    const token = localStorage.getItem("authToken");
    if (!token) {
      Swal.fire({
        title: "Authentication Required",
        text: "Please login to view your profile",
        icon: "warning",
        confirmButtonText: "Login",
      }).then(() => navigate("/"));
      return;
    }
    try {
      const res = await fetch(
        "https://evercorenutrition.com/ev_admin/api/customer/profile/",
        {
          method: "GET",
          headers: getAuthHeaders(token),
        },
      );
      if (res.status === 401) {
        ["authToken", "refreshToken", "userData"].forEach((k) =>
          localStorage.removeItem(k),
        );
        Swal.fire({
          title: "Session Expired",
          text: "Please login again",
          icon: "info",
          confirmButtonText: "Login",
        }).then(() => navigate("/"));
        return;
      }
      if (!res.ok) throw new Error("Failed to fetch profile data");
      const data = await res.json();
      if (data.customer) {
        const c = data.customer;
        setProfile({
          name:
            c.full_name ||
            `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
            "",
          first_name: c.first_name || "",
          last_name: c.last_name || "",
          email: c.email || "",
          phone: c.phone || "",
          gender: c.gender
            ? c.gender.charAt(0).toUpperCase() + c.gender.slice(1)
            : "Not specified",
          date_of_birth: c.date_of_birth || "",
        });
      }
      if (Array.isArray(data.addresses)) {
        const mapped = data.addresses.map((addr) => ({
          id: addr.id,
          label: "Address",
          receiver_name: addr.receiver_name || "",
          receiver_mobile: addr.receiver_mobile || "",
          line1: addr.street || "",
          city: addr.city || "",
          state: addr.state || "",
          country: addr.country || "India",
          zip: addr.zip_code || "",
          is_default_billing: addr.is_primary || false,
          is_default_shipping: addr.is_primary || false,
        }));
        setAddresses(mapped);
        const primary = mapped.find((a) => a.is_default_billing);
        setBillingAddress(primary || null);
        setShippingAddress(primary || null);
      }
      await fetchOrders();
    } catch (err) {
      setError(err.message);
      if (!isSilent)
        Swal.fire({
          title: "Error",
          text: "Failed to load profile data. Please try again.",
          icon: "error",
          timer: 2000,
          showConfirmButton: false,
        });
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  const fetchWishlist = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      let customerId = null;
      try {
        const ud = localStorage.getItem("userData");
        if (ud) {
          const p = JSON.parse(ud);
          customerId = p.id || p.customer_id || null;
        }
      } catch (e) {}
      if (!customerId) return;
      const res = await fetch(
        `https://evercorenutrition.com/ev_admin/api/wishlist/?customer_id=${customerId}`,
        { method: "GET", headers: getAuthHeaders(token) },
      );
      if (res.ok) {
        const data = await res.json();
        setWishlist(data?.wishlist?.products || []);
      }
    } catch (err) {
      console.error("fetchWishlist error:", err);
    }
  };

  const openModal = (type, item = null) => {
      console.log("MODAL TYPE =>", type);
      console.log("CURRENT ITEM DATA =>", item);
    setModalType(type);
    setCurrentItem(item);
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setCurrentItem(null);
  };

  // ── Newsletter Subscribe ──────────────────────────────────────
  const handleNewsletterSubscribe = async () => {
    const email = (profile.email || "").trim();
    if (!email || !validateEmail(email)) {
      Swal.fire({
        title: "Invalid Email",
        text: "No valid email found on your account.",
        icon: "warning",
        timer: 1500,
        showConfirmButton: false,
      });
      return;
    }

    setNewsletterLoading(true);
    try {
      const response = await fetch(
        "https://evercorenutrition.com/ev_admin/api/newsletter/subscribe/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: email }),
        },
      );

      const data = await response.json();
      console.log("Newsletter response:", data);

      if (response.ok) {
        setIsSubscribed(true);
        localStorage.setItem("newsletterSubscribed", "true");

        Swal.fire({
          title: "Subscribed!",
          text: data.message || "Successfully subscribed to our newsletter!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        closeModal();
      } else {
        // Handle 409 Conflict (already subscribed)
        if (response.status === 409) {
          setIsSubscribed(true);
          localStorage.setItem("newsletterSubscribed", "true");
          Swal.fire({
            title: "Already Subscribed",
            text:
              data.message ||
              "This email is already subscribed to our newsletter.",
            icon: "info",
            timer: 2000,
            showConfirmButton: false,
          });
          closeModal();
        } else {
          const errorMsg =
            data.message ||
            data.error ||
            "Failed to subscribe. Please try again.";
          Swal.fire({
            title: "Error",
            text: errorMsg,
            icon: "error",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      }
    } catch (err) {
      console.error("Newsletter subscription error:", err);
      Swal.fire({
        title: "Error",
        text: "Network error. Please try again.",
        icon: "error",
        timer: 1500,
        showConfirmButton: false,
      });
    } finally {
      setNewsletterLoading(false);
    }
  };

  // ── Newsletter Unsubscribe ────────────────────────────────────
  const handleNewsletterUnsubscribe = async () => {
    const email = (profile.email || "").trim();
    if (!email) return;

    const result = await Swal.fire({
      title: "Unsubscribe?",
      text: "You will stop receiving our newsletter emails.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, unsubscribe",
    });

    if (!result.isConfirmed) return;

    setNewsletterLoading(true);
    try {
      // Try the unsubscribe endpoint
      const response = await fetch(
        "https://evercorenutrition.com/ev_admin/api/newsletter/unsubscribe/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: email }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        setIsSubscribed(false);
        localStorage.removeItem("newsletterSubscribed");
        Swal.fire({
          title: "Unsubscribed",
          text:
            data.message || "You have been unsubscribed from our newsletter.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          title: "Error",
          text: data.message || data.error || "Failed to unsubscribe.",
          icon: "error",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error("Newsletter unsubscribe error:", err);
      Swal.fire({
        title: "Error",
        text: "Network error. Please try again.",
        icon: "error",
        timer: 1500,
        showConfirmButton: false,
      });
    } finally {
      setNewsletterLoading(false);
    }
  };
  // ── Validation ────────────────────────────────────────────────
  const validateProfileForm = () => {
    const firstName = document
      .getElementById("profileFirstName")
      ?.value?.trim();
    const lastName = document.getElementById("profileLastName")?.value?.trim();
    const email = document.getElementById("profileEmail")?.value?.trim();
    const phone = document.getElementById("profilePhone")?.value?.trim();
    if (!firstName) {
      Swal.fire({
        title: "Validation Error",
        text: "First name is required",
        icon: "warning",
        timer: 1500,
        showConfirmButton: false,
      });
      return false;
    }
    if (!lastName) {
      Swal.fire({
        title: "Validation Error",
        text: "Last name is required",
        icon: "warning",
        timer: 1500,
        showConfirmButton: false,
      });
      return false;
    }
    if (!email || !validateEmail(email)) {
      Swal.fire({
        title: "Invalid Email",
        text: "Please enter a valid email (e.g. name@example.com)",
        icon: "warning",
        timer: 1800,
        showConfirmButton: false,
      });
      return false;
    }
    if (!phone || !validatePhone(phone)) {
      Swal.fire({
        title: "Invalid Phone",
        text: "Enter a valid 10-digit Indian mobile number starting with 6-9",
        icon: "warning",
        timer: 1800,
        showConfirmButton: false,
      });
      return false;
    }
    return true;
  };

  const validateAddressForm = () => {
    const line1 = document.getElementById("addrLine1")?.value?.trim();
    const city = document.getElementById("addrCity")?.value?.trim();
    const state = document.getElementById("addrState")?.value?.trim();
    const country = document.getElementById("addrCountry")?.value;
    const zip = document.getElementById("addrZip")?.value?.trim();
    if (!line1) {
      Swal.fire({
        title: "Validation Error",
        text: "Street address is required",
        icon: "warning",
        timer: 1500,
        showConfirmButton: false,
      });
      return false;
    }
    if (!city) {
      Swal.fire({
        title: "Validation Error",
        text: "City is required",
        icon: "warning",
        timer: 1500,
        showConfirmButton: false,
      });
      return false;
    }
    if (!state) {
      Swal.fire({
        title: "Validation Error",
        text: "State is required",
        icon: "warning",
        timer: 1500,
        showConfirmButton: false,
      });
      return false;
    }
    if (!zip) {
      Swal.fire({
        title: "Validation Error",
        text: "Pincode is required",
        icon: "warning",
        timer: 1500,
        showConfirmButton: false,
      });
      return false;
    }
    if (country === "India" && !/^\d{6}$/.test(zip.replace(/\D/g, ""))) {
      Swal.fire({
        title: "Invalid Pincode",
        text: "Enter a valid 6-digit Indian pincode",
        icon: "warning",
        timer: 1500,
        showConfirmButton: false,
      });
      return false;
    }
    return true;
  };
  const handleAddAddress = async () => {
    const token = localStorage.getItem("authToken");

    if (!validateAddressForm()) return;

    try {
      let customerId = null;
      try {
        const userData = localStorage.getItem("userData");
        if (userData) {
          const parsed = JSON.parse(userData);
          customerId = String(parsed.id || parsed.customer_id || "");
        }
      } catch (e) {}

      if (!customerId) {
        Swal.fire({
          title: "Error",
          text: "Could not identify customer. Please login again.",
          icon: "error",
          timer: 1500,
          showConfirmButton: false,
        });
        return;
      }

      const line1 = document.getElementById("addrLine1")?.value?.trim();
      const city = document.getElementById("addrCity")?.value?.trim();
      const state = document.getElementById("addrState")?.value?.trim();
      const country = document.getElementById("addrCountry")?.value;
      const zip = document.getElementById("addrZip")?.value?.trim();
      const label = document.getElementById("addrLabel")?.value;
      const receiver_name = document.getElementById("receiver_name")?.value?.trim();
      const receiver_mobile = document.getElementById("receiver_mobile")?.value?.trim();

      const response = await fetch(
        "https://evercorenutrition.com/ev_admin/api/customer/addresses/",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customer_id: customerId,
            receiver_name: receiver_name,
            receiver_mobile: receiver_mobile,
            street: line1,
            city: city,
            state: state,
            country: country,
            zip_code: zip,
            address_type: label?.toLowerCase() || "home",
            is_primary: addresses.length === 0,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();

        const newAddress = {
          id: data.address?.id || data.id,
          label: label,
          receiver_name: receiver_name,
  receiver_mobile: receiver_mobile,
          line1: line1,
          city: city,
          state: state,
          country: country,
          zip: zip,
          is_default_billing: addresses.length === 0,
          is_default_shipping: addresses.length === 0,
        };

        setAddresses([...addresses, newAddress]);

        Swal.fire({
          title: "Success!",
          text: "Address added successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        closeModal();
        // await silentRefresh();
      } else {
        const errorData = await response.json().catch(() => ({}));
        Swal.fire({
          title: "Error!",
          text: errorData.message || "Failed to add address",
          icon: "error",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error("Add address error:", err);
      Swal.fire({
        title: "Error",
        text: "Failed to add address. Please try again.",
        icon: "error",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const handleEditAddress = async () => {
    const token = localStorage.getItem("authToken");

    if (!validateAddressForm()) return;

    try {
      let customerId = null;
      try {
        const userData = localStorage.getItem("userData");
        if (userData) {
          const parsed = JSON.parse(userData);
          customerId = String(parsed.id || parsed.customer_id || "");
        }
      } catch (e) {}

      if (!customerId || !currentItem?.id) {
        Swal.fire({
          title: "Error",
          text: "Could not identify address or customer.",
          icon: "error",
          timer: 1500,
          showConfirmButton: false,
        });
        return;
      }

      const line1 = document.getElementById("addrLine1")?.value?.trim();
      const city = document.getElementById("addrCity")?.value?.trim();
      const state = document.getElementById("addrState")?.value?.trim();
      const country = document.getElementById("addrCountry")?.value;
      const zip = document.getElementById("addrZip")?.value?.trim();
      const label = document.getElementById("addrLabel")?.value;
      const receiver_name = document.getElementById("receiver_name")?.value?.trim();
      const receiver_mobile = document.getElementById("receiver_mobile")?.value?.trim();

      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/customer/addresses/${currentItem.id}/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customer_id: customerId,
            receiver_name: receiver_name,
  receiver_mobile: receiver_mobile,
            street: line1,
            city: city,
            state: state,
            country: country,
            zip_code: zip,
            address_type: label?.toLowerCase() || "home",
            is_primary: currentItem.is_default_billing,
          }),
        },
      );

      if (response.ok) {
        const updatedAddresses = addresses.map((addr) =>
          addr.id === currentItem.id
            ? {
                ...addr,
                label: label,
                receiver_name: receiver_name,
        receiver_mobile: receiver_mobile,
                line1: line1,
                city: city,
                state: state,
                country: country,
                zip: zip,
              }
            : addr,
        );

        setAddresses(updatedAddresses);

        if (billingAddress?.id === currentItem.id) {
          setBillingAddress(
            updatedAddresses.find((a) => a.id === currentItem.id),
          );
        }
        if (shippingAddress?.id === currentItem.id) {
          setShippingAddress(
            updatedAddresses.find((a) => a.id === currentItem.id),
          );
        }

        Swal.fire({
          title: "Success!",
          text: "Address updated successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        closeModal();
        // await silentRefresh();
      } else {
        const errorData = await response.json().catch(() => ({}));
        Swal.fire({
          title: "Error!",
          text: errorData.message || "Failed to update address",
          icon: "error",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error("Edit address error:", err);
      Swal.fire({
        title: "Error",
        text: "Failed to update address. Please try again.",
        icon: "error",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem("authToken");

    try {
      // ── Handle Newsletter Subscription ──
      if (modalType === "subscribe-newsletter") {
        // ADD THIS VALIDATION CHECK
        const email = (profile.email || "").trim();
        if (!email || !validateEmail(email)) {
          Swal.fire({
            title: "Invalid Email",
            text: "Please update your profile with a valid email address first. Format: name@example.com",
            icon: "warning",
            confirmButtonText: "OK",
          });
          return; // Stop execution
        }
        await handleNewsletterSubscribe();
        return;
      }

      // Handle Add Address
      if (modalType === "add-address") {
        await handleAddAddress();
        return;
      }

      // Handle Edit Address
      if (modalType === "edit-address") {
        await handleEditAddress();
        return;
      }

      if (modalType === "edit-profile") {
        let customerId = null;
        try {
          const userData = localStorage.getItem("userData");
          if (userData) {
            const parsed = JSON.parse(userData);
            customerId = String(parsed.id || parsed.customer_id || "");
          }
        } catch (e) {}

        if (!customerId) {
          Swal.fire({
            title: "Error",
            text: "Could not identify customer. Please login again.",
            icon: "error",
            timer: 1500,
            showConfirmButton: false,
          });
          return;
        }

        const firstName =
          document.getElementById("profileFirstName")?.value ||
          profile.first_name;
        const lastName =
          document.getElementById("profileLastName")?.value ||
          profile.last_name;
        const updatedEmail =
          document.getElementById("profileEmail")?.value || profile.email;
        const updatedPhone =
          document.getElementById("profilePhone")?.value || profile.phone;
        const updatedGender =
          document.getElementById("profileGender")?.value ||
          profile.gender.toLowerCase();

        // OLD DOB SCRIPT
        // const updatedDOB =
        //   document.getElementById("profileDOB")?.value ||
        //   profile.date_of_birth ||
        //   "";

        // NEW DOB SCRIPT
        let updatedDOB = document.getElementById("profileDOB")?.value || "";
        if (updatedDOB === "") {
          updatedDOB = null;
        }



        // ADD EMAIL VALIDATION HERE
        if (!validateEmail(updatedEmail)) {
          Swal.fire({
            title: "Invalid Email Address",
            text: "Please enter a valid email address (e.g., name@example.com, name@domain.co.in).",
            icon: "warning",
            confirmButtonText: "OK",
          });
          return; // Stop execution
        }

        const response = await fetch(
          "https://evercorenutrition.com/ev_admin/api/customer/profile/edit/",
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              customer_id: customerId,
              first_name: firstName,
              last_name: lastName,
              email: updatedEmail,
              phone: updatedPhone,
              gender: updatedGender,
              date_of_birth: updatedDOB,
            }),
          },
        );

        if (response.ok) {
          setProfile({
            ...profile,
            name: `${firstName} ${lastName}`.trim(),
            first_name: firstName,
            last_name: lastName,
            email: updatedEmail,
            phone: updatedPhone,
            gender:
              updatedGender.charAt(0).toUpperCase() + updatedGender.slice(1),
            date_of_birth: updatedDOB,
          });
          Swal.fire({
            title: "Success!",
            text: "Profile updated successfully.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
          closeModal();
          await silentRefresh();
        } else {
          const errorData = await response.json();
          Swal.fire({
            title: "Error!",
            text: errorData.message || "Failed to update profile",
            icon: "error",
            timer: 1500,
            showConfirmButton: false,
          });
        }
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "Failed to save changes. Please try again.",
        icon: "error",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const handleDeleteAddress = async (addressId) => {
    const token = localStorage.getItem("authToken");
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this address?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(
        `https://evercorenutrition.com/ev_admin/api/customer/addresses/${addressId}/`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        const updated = addresses.filter((x) => x.id !== addressId);
        setAddresses(updated);
        if (billingAddress?.id === addressId)
          setBillingAddress(updated[0] || null);
        if (shippingAddress?.id === addressId)
          setShippingAddress(updated[0] || null);
        Swal.fire({
          title: "Deleted!",
          text: "Address removed successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        await silentRefresh();
      } else {
        Swal.fire({
          title: "Error!",
          text: "Failed to delete address.",
          icon: "error",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "Failed to delete address.",
        icon: "error",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const formatDateForDisplay = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
  };

  const getModalForm = () => {
    if (modalType === "edit-profile") {
      return (
        <div className="row g-4">
          <div className="col-md-6">
            <label className="modern-form-label">First Name </label>
            <input
              type="text"
              className="modern-form-control"
              defaultValue={profile.first_name}
              id="profileFirstName"
              placeholder="Enter first name"
            />
          </div>
          <div className="col-md-6">
            <label className="modern-form-label">Last Name </label>
            <input
              type="text"
              className="modern-form-control"
              defaultValue={profile.last_name}
              id="profileLastName"
              placeholder="Enter last name"
            />
          </div>
          <div className="col-12">
            <label className="modern-form-label">Email Address *</label>
            <input
              type="email"
              className="modern-form-control"
              defaultValue={profile.email}
              id="profileEmail"
              placeholder="e.g., name@example.com"
              onBlur={(e) => {
                const email = e.target.value.trim();
                if (email && !validateEmail(email)) {
                  Swal.fire({
                    title: "Invalid Email",
                    text: "Please enter a valid email address (e.g., name@example.com, name@domain.co.in)",
                    icon: "warning",
                    timer: 2000,
                    showConfirmButton: false,
                  });
                  e.target.style.borderColor = "#dc3545";
                } else {
                  e.target.style.borderColor = "#e0e0e0";
                }
              }}
              onChange={(e) => {
                const email = e.target.value;
                if (email && validateEmail(email)) {
                  e.target.style.borderColor = "#28a745";
                } else if (email) {
                  e.target.style.borderColor = "#dc3545";
                } else {
                  e.target.style.borderColor = "#e0e0e0";
                }
              }}
            />
            <small className="text-muted">
              Valid formats: name@example.com, name@domain.co.in,
              name@sub.domain.org
            </small>
          </div>
          <div className="col-12">
            <label className="modern-form-label">Phone Number </label>
            <input
              type="tel"
              className="modern-form-control"
              defaultValue={profile.phone}
              id="profilePhone"
              placeholder="10-digit mobile number"
              maxLength={10}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
              }}
            />
            <small className="text-muted">
              10-digit Indian mobile number (starts with 6–9)
            </small>
          </div>
          <div className="col-12">
            <label className="modern-form-label">Gender</label>
            <select
              className="modern-form-control modern-select"
              defaultValue={(profile.gender || "male").toLowerCase()}
              id="profileGender"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="col-12">
            <label className="modern-form-label">Date of Birth</label>
            <input
              type="date"
              className="modern-form-control"
              defaultValue={profile.date_of_birth || ""}
              id="profileDOB"
              // max={
              //   new Date(new Date().setFullYear(new Date().getFullYear() - 10))
              //     .toISOString()
              //     .split("T")[0]
              // }
            />
          </div>
        </div>
      );
    }

    if (modalType === "upload-photo") {
      return (
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              margin: "0 auto 20px",
              background: "linear-gradient(135deg,#667eea 20%,#764ba2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaCamera size={40} color="#fff" />
          </div>
          <label className="modern-form-label">
            Select New Profile Picture
          </label>
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif"
            className="modern-form-control"
            id="uploadPhotoInput"
            style={{ padding: "10px" }}
          />
          <p style={{ color: "#666", fontSize: "13px", marginTop: "10px" }}>
            Supported: JPG, PNG, GIF. Max 5MB
          </p>
        </div>
      );
    }

    // Replace the newsletter modal form section in getModalForm()
    if (modalType === "subscribe-newsletter") {
      return (
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <div
            style={{
              width: "70px",
              height: "70px",
              borderRadius: "50%",
              margin: "0 auto 20px",
              background: "linear-gradient(135deg,#0d6efd 0%,#6610f2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaEnvelope size={28} color="#fff" />
          </div>
          <h5 style={{ fontWeight: "600", marginBottom: "8px" }}>
            Subscribe to Newsletter
          </h5>
          <p style={{ color: "#666", fontSize: "14px", marginBottom: "16px" }}>
            Stay updated with our latest products, offers, and news.
            <br />
            We'll send updates to: <strong>{profile.email}</strong>
          </p>
          <div
            style={{
              background: "#f0f7ff",
              borderRadius: "10px",
              padding: "12px 16px",
              fontSize: "13px",
              color: "#0d6efd",
              border: "1px solid #cce0ff",
              textAlign: "left",
            }}
          >
            <FaCheck style={{ marginRight: "6px" }} /> No spam. Unsubscribe
            anytime.
          </div>
          {error && (
            <p
              style={{ marginTop: "15px", color: "#dc3545", fontSize: "14px" }}
            >
              <FaExclamationCircle style={{ marginRight: "6px" }} />
              {error}
            </p>
          )}
        </div>
      );
    }

    if (modalType === "add-address" || modalType === "edit-address") {
      return (
        <div className="row g-4">
          <div className="col-12">
            <label className="modern-form-label">Address Label</label>
            <select
              className="modern-form-control modern-select"
              defaultValue={currentItem?.label || "Home"}
              id="addrLabel"
            >
              <option value="Home">Home</option>
              <option value="Office">Office</option>
              <option value="Other">Other</option>
            </select>
          </div>
           <div className="col-6">
            <label className="modern-form-label">Full Name <span className="text-danger">*</span> </label>
            <input
              type="text"
              className="modern-form-control"
              value={currentItem?.receiver_name || ""}
  onChange={(e) =>
    setCurrentItem({
      ...currentItem,
      receiver_name: e.target.value,
    })
  }
              id="receiver_name"
              placeholder="Enter Receiver's Full Name"
            />
          </div>

           <div className="col-6">
            <label className="modern-form-label">Contact  <span className="text-danger">*</span> </label>
            <input
              type="tel"
              className="modern-form-control"
              value={currentItem?.receiver_mobile || ""}
  onChange={(e) =>
    setCurrentItem({
      ...currentItem,
      receiver_mobile: e.target.value,
    })
  }
              id="receiver_mobile"
              placeholder="Enter Receiver's Mobile Number"
            />
          </div>

          <div className="col-12">
            <label className="modern-form-label">Street Address  <span className="text-danger">*</span> </label>
            <input
              type="text"
              className="modern-form-control"
              defaultValue={currentItem?.line1 || ""}
              id="addrLine1"
              placeholder="Enter street address"
            />
          </div>
          <div className="col-6">
            <label className="modern-form-label">City  <span className="text-danger">*</span> </label>
            <input
              type="text"
              className="modern-form-control"
              defaultValue={currentItem?.city || ""}
              id="addrCity"
              placeholder="Enter city"
            />
          </div>
          <div className="col-6">
            <label className="modern-form-label">State  <span className="text-danger">*</span> </label>
            <input
              type="text"
              className="modern-form-control"
              defaultValue={currentItem?.state || ""}
              id="addrState"
              placeholder="Enter state"
            />
          </div>
          <div className="col-6">
            <label className="modern-form-label">Country</label>
            <select
              className="modern-form-control modern-select"
              defaultValue={currentItem?.country || "India"}
              id="addrCountry"
            >
              <option value="India">India</option>
              <option value="United States">United States</option>
              <option value="United Kingdom">United Kingdom</option>
              <option value="Canada">Canada</option>
              <option value="Australia">Australia</option>
            </select>
          </div>
          <div className="col-6">
            <label className="modern-form-label">Pincode  <span className="text-danger">*</span> </label>
            <input
              type="text"
              className="modern-form-control"
              defaultValue={currentItem?.zip || ""}
              id="addrZip"
              placeholder="6-digit pincode"
              maxLength={6}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
              }}
            />
            <small className="text-muted">6-digit pincode</small>
          </div>
        </div>
      );
    }
  };

  const EmptyState = ({ message, Icon, action }) => (
    <div
      style={{
        textAlign: "center",
        padding: "60px 20px",
        background: "#f8f9fa",
        borderRadius: "12px",
        margin: "20px 0",
      }}
    >
      <div style={{ marginBottom: "20px", opacity: 0.5 }}>
        {Icon ? (
          <Icon size={48} color="#999" />
        ) : (
          <FaBoxOpen size={48} color="#999" />
        )}
      </div>
      <h4 style={{ color: "#333", marginBottom: "10px", fontWeight: "500" }}>
        {message || "No items found"}
      </h4>
      {action && (
        <button
          onClick={action.onClick}
          style={{
            marginTop: "15px",
            padding: "10px 25px",
            background: "#000",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: "10px", color: "#666" }}>
            Loading your profile...
          </p>
        </div>
      </div>
    );

  if (error)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <FaExclamationCircle
            size={48}
            color="#dc3545"
            style={{ marginBottom: "20px" }}
          />
          <h3>Something went wrong</h3>
          <p style={{ color: "#666", marginBottom: "20px" }}>{error}</p>
          <button
            onClick={fetchProfileData}
            style={{
              padding: "10px 20px",
              background: "#000",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );

  const sections = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <FaTachometerAlt />,
      content: (
        <div style={{ padding: "20px" }}>
          <div className="dashboard-home">
            <div className="title">
              <h2>My Account</h2>
              <span className="title-leaf">
                <svg className="icon-width bg-gray">
                  <use href="../assets/svg/leaf.svg#leaf"></use>
                </svg>
              </span>
            </div>
            <div className="dashboard-user-name">
              <h6 className="text-content">
                Hello, <b className="text-title">{profile.name || "User"}</b>
              </h6>
              <p className="text-content">
                From your My Account Dashboard you can view recent account
                activity and update account information.
              </p>
            </div>
            <div className="total-box">
              <div className="row g-sm-4 g-3">
                {[
                  {
                    img: "order.svg",
                    label: "Total Order",
                    count: orders.length,
                  },
                  {
                    img: "pending.svg",
                    label: "Pending Orders",
                    count: pendingOrders.length,
                  },
                  {
                    img: "wishlist.svg",
                    label: "Wishlist",
                    count: wishlist.length,
                  },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="col-xxl-4 col-lg-6 col-md-4 col-sm-6"
                  >
                    <div
                      className="total-contain"
                      style={{
                        background: "#fff",
                        padding: "20px",
                        borderRadius: "12px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                      }}
                    >
                      <img
                        src={`/assets/images/${stat.img}`}
                        className="img-1"
                        alt={stat.label}
                        style={{ width: "45px" }}
                      />
                      <div className="total-detail">
                        <h5 style={{ margin: 0, color: "#000" }}>
                          {stat.label}
                        </h5>
                        <h3
                          className="text-title"
                          style={{ margin: "5px 0 0 0" }}
                        >
                          {stat.count}
                        </h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="dashboard-title" style={{ marginTop: "25px" }}>
              <h3>Account Information</h3>
            </div>
            <div className="row g-4">
              <div className="col-xxl-6">
                <div
                  className="dashboard-content-title"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <h4>Contact Information</h4>
                  </div>
                  <p
                    onClick={() => openModal("edit-profile", profile)}
                    style={{
                      color: "#0d6efd",
                      marginBottom: "0",
                      cursor: "pointer",
                    }}
                  >
                    Edit
                  </p>
                </div>
                <hr style={{ marginTop: "0" }} />
                <div className="dashboard-detail">
                  <h6 className="text-content" style={{ marginBottom: "5px" }}>
                    {profile.name || "Not provided"}
                  </h6>
                  <h6 className="text-content" style={{ marginBottom: "5px" }}>
                    {profile.email || "Not provided"}
                  </h6>
                  <h6 className="text-content" style={{ marginBottom: "5px" }}>
                    {profile.phone || "Not provided"}
                  </h6>
                  <h6 className="text-content" style={{ marginBottom: "5px" }}>
                    <strong>Date of Birth:</strong>{" "}
                    {formatDateForDisplay(profile.date_of_birth) ||
                      "Not provided"}
                  </h6>
                </div>
              </div>
              <div className="col-xxl-6">
                <div
                  className="dashboard-content-title"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <div>
                    <h4 style={{ margin: 0 }}>Newsletters</h4>
                  </div>
                  <div>
                    {isSubscribed ? (
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={handleNewsletterUnsubscribe}
                        disabled={newsletterLoading}
                        style={{
                          whiteSpace: "nowrap",
                          fontSize: "12px",
                          padding: "4px 10px",
                        }}
                      >
                        {newsletterLoading ? (
                          <span className="spinner-border spinner-border-sm me-1" />
                        ) : (
                          <FaBellSlash style={{ marginRight: "4px" }} />
                        )}
                        Unsubscribe
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => openModal("subscribe-newsletter")}
                        disabled={newsletterLoading}
                        style={{
                          whiteSpace: "nowrap",
                          fontSize: "12px",
                          padding: "4px 10px",
                        }}
                      >
                        <FaBell style={{ marginRight: "4px" }} /> Subscribe
                      </button>
                    )}
                  </div>
                </div>
                <hr style={{ marginTop: "8px", marginBottom: "8px" }} />
                <div className="dashboard-detail">
                  <h6
                    className="text-content"
                    style={
                      isSubscribed
                        ? { color: "#28a745", fontSize: "13px" }
                        : { fontSize: "13px" }
                    }
                  >
                    {isSubscribed ? (
                      <>
                        <FaCheck style={{ marginRight: "6px" }} />
                        You are subscribed to our newsletter.
                      </>
                    ) : (
                      "You are currently not subscribed to any newsletter"
                    )}
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "profile",
      label: "My Profile",
      icon: <FaUser />,
      content: (
        <div className="info-grid">
          <div className="profile-card" style={{ position: "relative" }}>
            <div
              onClick={() => openModal("edit-profile", profile)}
              style={{
                position: "absolute",
                top: "15px",
                right: "15px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "#f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "1px solid #e0e0e0",
              }}
            >
              <FaPencilAlt size={14} color="#555" />
            </div>
            <div className="profile-details">
              <h4>{profile.name || "User"}</h4>
              <p>
                <strong>Email:</strong> {profile.email || "Not provided"}
              </p>
              <p>
                <strong>Phone:</strong> {profile.phone || "Not provided"}
              </p>
              <p>
                <strong>Gender:</strong> {profile.gender || "Not specified"}
              </p>
              <p>
                <strong>Date of Birth:</strong>{" "}
                {formatDateForDisplay(profile.date_of_birth) || "Not provided"}
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "address",
      label: "My Address",
      icon: <FaMapMarkerAlt />,
      content: (
        <>
          <button
            className="pro-acc-add-btn"
            onClick={() => openModal("add-address")}
          >
            + Add New Address
          </button>
          {addresses.length > 0 ? (
            <div className="info-grid">
              {addresses.map((a) => (
                <div className="address-card" key={a.id}>
                  <h4>{a.label}</h4>

                  <p><strong>Name:</strong> {a.receiver_name || '-'}</p>
<p><strong>Mobile:</strong> {a.receiver_mobile || '-'}</p>
                  <p>{a.line1}</p>
                  <p>
                    {a.city}, {a.state}
                  </p>
                  <p>
                    {a.country} - {a.zip}
                  </p>
                  <div style={{ marginTop: "10px" }}>
                    {a.is_default_billing && (
                      <span className="badge bg-primary me-2">
                        Default Billing
                      </span>
                    )}
                    {a.is_default_shipping && (
                      <span className="badge bg-success">Default Shipping</span>
                    )}
                  </div>
                  <button
                    className="pro-acc-edit-btn"
                    onClick={() => openModal("edit-address", a)}
                  >
                    <FaPencilAlt />
                  </button>
                  <button
                    className="pro-acc-remove-btn"
                    onClick={() => handleDeleteAddress(a.id)}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              message="No addresses found"
              Icon={FaMapMarkerAlt}
              action={{
                label: "Add New Address",
                onClick: () => openModal("add-address"),
              }}
            />
          )}
        </>
      ),
    },
    {
      id: "returns",
      label: "My Returns",
      icon: <FaUndo />,
      content: <ReturnsSection orders={orders} />,
    },
    {
      id: "help",
      label: "Help & Support",
      icon: <FaHeadset />,
      content: (
        <div className="dashboard-card">
          <div className="title" style={{ marginBottom: "0px" }}>
            <h2>Help & Support</h2>
            <span className="title-leaf">
              <svg className="icon-width bg-gray">
                <use href="../assets/svg/leaf.svg#leaf"></use>
              </svg>
            </span>
          </div>
          <div className="faq-accordion mt-4">
            <div className="accordion" id="accordionExample">
              {[
                {
                  id: "One",
                  open: true,
                  q: "What is Evercore and what do we offer?",
                  a: "<strong>Evercore</strong> is a premium fitness and nutrition brand offering high-quality protein supplements, pre-workouts, BCAAs, vitamins, and performance-enhancing products.",
                },
                {
                  id: "Two",
                  open: false,
                  q: "How do I place an order on Evercore?",
                  a: "Simply browse your favorite products, add them to your cart, proceed to checkout, enter your delivery information, and complete the payment.",
                },
                {
                  id: "Three",
                  open: false,
                  q: "What payment methods does Evercore accept?",
                  a: "We accept all major payment modes including <strong>UPI, Debit/Credit Cards, Net Banking, Wallets</strong>, and Cash on Delivery.",
                },
                {
                  id: "Four",
                  open: false,
                  q: "How long does Evercore take to deliver orders?",
                  a: "Delivery usually takes <strong>2–5 business days</strong> depending on your city and product availability.",
                },
                {
                  id: "Five",
                  open: false,
                  q: "How can I track my Evercore order?",
                  a: 'After your order is shipped, you\'ll receive a tracking link through SMS or email. You can also check order status in the <strong>"My Orders"</strong> section.',
                },
                {
                  id: "Six",
                  open: false,
                  q: "What is Evercore's return and refund policy?",
                  a: "You can request a return or replacement within <strong>7 days</strong> if the product is damaged, incorrect, or sealed and unused.",
                },
                {
                  id: "Seven",
                  open: false,
                  q: "Is my information safe with Evercore?",
                  a: "Yes! We follow advanced encryption and security protocols to protect your personal and payment information.",
                },
                {
                  id: "Eight",
                  open: false,
                  q: "Do you offer discounts or loyalty rewards?",
                  a: "Yes! Evercore offers seasonal discounts, exclusive offers, promo codes, and loyalty rewards for repeat customers.",
                },
                {
                  id: "Nine",
                  open: false,
                  q: "How can I contact Evercore support?",
                  a: 'You can reach us through the <a href="/contact">Contact Us</a> page, email us at <strong>support@evercore.com</strong>, or call between <strong>9 AM – 9 PM</strong>.',
                },
              ].map((item) => (
                <div className="accordion-item" key={item.id}>
                  <h2 className="accordion-header">
                    <button
                      className={`accordion-button${item.open ? "" : " collapsed"}`}
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#collapse${item.id}`}
                    >
                      {item.q}
                    </button>
                  </h2>
                  <div
                    id={`collapse${item.id}`}
                    className={`accordion-collapse collapse${item.open ? " show" : ""}`}
                    data-bs-parent="#accordionExample"
                  >
                    <div className="accordion-body">
                      <p dangerouslySetInnerHTML={{ __html: item.a }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "orders",
      label: "My Orders",
      icon: <FaShoppingBag />,
      content: (
        <>
          {orders.length > 0 ? (
            <div className="pro-orders-grid">
              {orders.map((order) => {
                // Helper function to get the correct image URL
                const getOrderImage = () => {
                  // Try to get image from available sources
                  if (order.is_combo_order) {
                    return order.combo_image || null;
                  } else {
                    return order.first_item_image || order.combo_image || null;
                  }
                };

                const orderImage = getOrderImage();

                // Get payment status configuration
                const getPaymentStatusConfig = (status) => {
                  const statusLower = status?.toLowerCase() || "";
                  switch (statusLower) {
                    case "completed":
                      return {
                        color: "#10b981",
                        bg: "#d1fae5",
                        label: "Payment Completed",
                        icon: "✓",
                      };
                    case "collected":
                      return {
                        color: "#10b981",
                        bg: "#d1fae5",
                        label: "Payment Collected",
                        icon: "✓",
                      };
                    case "pending":
                      return {
                        color: "#f59e0b",
                        bg: "#fef3c7",
                        label: "Payment Pending",
                        icon: "⏳",
                      };
                    case "failed":
                      return {
                        color: "#ef4444",
                        bg: "#fee2e2",
                        label: "Payment Failed",
                        icon: "✗",
                      };
                    case "refunded":
                      return {
                        color: "#8b5cf6",
                        bg: "#ede9fe",
                        label: "Refunded",
                        icon: "↺",
                      };
                    case "cancelled":
                      return {
                        color: "#ef4444",
                        bg: "#fee2e2",
                        label: "Payment Cancelled",
                        icon: "✗",
                      };
                    default:
                      return {
                        color: "#6b7280",
                        bg: "#f3f4f6",
                        label: order.payment_status_display || "Unknown",
                        icon: "?",
                      };
                  }
                };

                const paymentConfig = getPaymentStatusConfig(
                  order.payment_status,
                );

                // Format total amount
                const totalAmount = parseFloat(order.total_amount || 0).toFixed(
                  2,
                );

                return (
                  <div
                    className="pro-order-card"
                    key={order.id || order.order_id}
                  >
                    {/* Only render image if it exists */}
                    {/* Order Image - Only render if image exists */}
                    {orderImage && (
                      <div className="order-image-container">
                        <img
                          src={getImageUrl(orderImage)}
                          alt={`Order ${order.order_id}`}
                          className="pro-order-img"
                        />
                      </div>
                    )}
                    <div className="pro-order-info">
                      <div className="pro-order-header">
                        <h4>Order #{order.order_id}</h4>
                        {/* Payment Status Badge */}
                        <span
                          className="payment-status-badge"
                          style={{
                            backgroundColor: paymentConfig.bg,
                            color: paymentConfig.color,
                            padding: "4px 10px",
                            borderRadius: "20px",
                            fontSize: "11px",
                            fontWeight: "600",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>{paymentConfig.icon}</span>
                          {paymentConfig.label}
                        </span>
                      </div>

                      {/* Show combo badge if it's a combo order */}
                      {order.is_combo_order && order.combo_name && (
                        <div style={{ marginBottom: "8px" }}>
                          <span
                            style={{
                              background: "#f0f0f0",
                              color: "#666",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "11px",
                              fontWeight: "500",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <FaLayerGroup size={11} /> Combo: {order.combo_name}
                          </span>
                        </div>
                      )}

                      <p className="price">₹{totalAmount}</p>
                      <p
                        className={`status pro-status ${order.status?.toLowerCase()}`}
                      >
                        {order.status_display || order.status}
                      </p>
                      <p className="order-date">
                        <small>
                          {new Date(order.created_at).toLocaleDateString(
                            "en-IN",
                            { day: "numeric", month: "short", year: "numeric" },
                          )}
                        </small>
                      </p>
                      <p className="items-count">
                        <small>{order.total_items || 0} item(s)</small>
                      </p>
                      <div className="pro-order-actions">
                        <button
                          onClick={() => handleTrackOrder(order.order_id)}
                          style={{
                            padding: "8px 16px",
                            background: "#0d6efd",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            marginRight: "10px",
                          }}
                        >
                          Track Order
                        </button>
                        <button
                          onClick={() => navigate(`/orders/${order.order_id}`)}
                          style={{
                            padding: "8px 16px",
                            background: "#6c757d",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              message="No orders yet"
              Icon={FaShoppingCart}
              action={{
                label: "Start Shopping",
                onClick: () => navigate("/shop"),
              }}
            />
          )}
        </>
      ),
    },
    {
      id: "wishlist",
      label: "Wishlist",
      icon: <FaHeart />,
      content: (
        <>
          {wishlist.length > 0 ? (
            <div className="pro-wishlist-grid">
              {wishlist.map((item) => {
                const imgUrl = item.image;
                const fullImg = imgUrl
                  ? imgUrl.startsWith("http")
                    ? imgUrl
                    : `https://evercorenutrition.com${imgUrl}`
                  : null;
                return (
                  <div className="pro-wishlist-card" key={item.id}>
                    <img
                      src={fullImg}
                      alt={item.name}
                      className="pro-wishlist-img"
                    />
                    <div className="pro-wishlist-info">
                      <h4>{item.name}</h4>
                      <p className="price">
                        ₹
                        {parseFloat(
                          item.selling_price || item.price || 0,
                        ).toLocaleString("en-IN")}
                        {item.price > item.selling_price && (
                          <span
                            style={{
                              textDecoration: "line-through",
                              color: "#999",
                              marginLeft: "8px",
                              fontSize: "0.85em",
                            }}
                          >
                            ₹
                            {parseFloat(item.price || 0).toLocaleString(
                              "en-IN",
                            )}
                          </span>
                        )}
                      </p>
                      <div className="pro-wishlist-actions">
                        <button
                          onClick={async () => {
                            const token = localStorage.getItem("authToken");
                            let customerId = null;
                            try {
                              const ud = localStorage.getItem("userData");
                              if (ud) {
                                const p = JSON.parse(ud);
                                customerId = String(
                                  p.id || p.customer_id || "",
                                );
                              }
                            } catch (e) {}
                            if (!customerId) {
                              Swal.fire({
                                title: "Error",
                                text: "Please login again.",
                                icon: "error",
                                timer: 1500,
                                showConfirmButton: false,
                              });
                              return;
                            }
                            const res = await fetch(
                              "https://evercorenutrition.com/ev_admin/api/cart/",
                              {
                                method: "POST",
                                headers: getAuthHeaders(token),
                                body: JSON.stringify({
                                  customer_id: customerId,
                                  product_id: item.id,
                                  quantity: 1,
                                }),
                              },
                            );
                            if (res.ok) {
                              Swal.fire({
                                title: "Added!",
                                text: "Item added to cart.",
                                icon: "success",
                                timer: 1500,
                                showConfirmButton: false,
                              });
                            } else {
                              const err = await res.json().catch(() => ({}));
                              const msg = err.error || err.message || "";
                              Swal.fire({
                                title: msg.includes("Duplicate")
                                  ? "Already in Cart"
                                  : "Error",
                                text: msg.includes("Duplicate")
                                  ? "This item is already in your cart."
                                  : msg || "Failed to add to cart.",
                                icon: msg.includes("Duplicate")
                                  ? "info"
                                  : "error",
                                timer: 1500,
                                showConfirmButton: false,
                              });
                            }
                            await silentRefresh();
                          }}
                        >
                          Add to Cart
                        </button>
                        <button
                          onClick={async () => {
                            const token = localStorage.getItem("authToken");
                            let customerId = null;
                            try {
                              const ud = localStorage.getItem("userData");
                              if (ud) {
                                const p = JSON.parse(ud);
                                customerId = p.id || p.customer_id || null;
                              }
                            } catch (e) {}
                            if (!customerId) return;
                            const res = await fetch(
                              `https://evercorenutrition.com/ev_admin/api/wishlist/?customer_id=${customerId}&product_id=${item.id}`,
                              {
                                method: "DELETE",
                                headers: getAuthHeaders(token),
                              },
                            );
                            if (res.ok) {
                              setWishlist((prev) =>
                                prev.filter((w) => w.id !== item.id),
                              );
                              Swal.fire({
                                title: "Removed!",
                                text: "Item removed from wishlist.",
                                icon: "success",
                                timer: 1500,
                                showConfirmButton: false,
                              });
                            } else {
                              Swal.fire({
                                title: "Error",
                                text: "Failed to remove item.",
                                icon: "error",
                                timer: 1500,
                                showConfirmButton: false,
                              });
                            }
                            await silentRefresh();
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              message="Your wishlist is empty"
              Icon={FaHeart}
              action={{
                label: "Explore Products",
                onClick: () => navigate("/shop"),
              }}
            />
          )}
        </>
      ),
    },
  ];

  return (
    <div>
      <div className="pro-acc-wrapper">
        {!isMobile && (
          <div className="pro-acc-sidebar">
            <div className="pro-acc-tabs">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`pro-acc-tab ${activeSection === section.id ? "active" : ""}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <span className="pro-acc-tab-icon">{section.icon}</span>
                  {section.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {isMobile && sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 9998,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                bottom: 0,
                width: "260px",
                backgroundColor: "#fff",
                zIndex: 9999,
                display: "flex",
                flexDirection: "column",
                boxShadow: "4px 0 20px rgba(0,0,0,0.15)",
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "16px 20px",
                  borderBottom: "1px solid #eee",
                  position: "sticky",
                  top: 0,
                  background: "#fff",
                  zIndex: 1,
                }}
              >
                <span style={{ fontWeight: "700", fontSize: "16px" }}>
                  My Account
                </span>
                <button
                  onClick={() => setSidebarOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                  }}
                >
                  <FaTimes size={16} color="#333" />
                </button>
              </div>
              <div style={{ padding: "10px 0", overflowY: "auto" }}>
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => {
                      setActiveSection(section.id);
                      setSidebarOpen(false);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      width: "100%",
                      padding: "14px 20px",
                      background:
                        activeSection === section.id
                          ? "#f0f0f0"
                          : "transparent",
                      border: "none",
                      borderBottom: "1px solid #f5f5f5",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: activeSection === section.id ? "600" : "400",
                      color: "#333",
                      textAlign: "left",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "16px",
                        color: activeSection === section.id ? "#000" : "#666",
                      }}
                    >
                      {section.icon}
                    </span>
                    {section.label}
                  </button>
                ))}

                {/* LOGOUT BUTTON */}

                <button
                  onClick={() => {
                    setSidebarOpen(false);
                    [
                      "authToken",
                      "refreshToken",
                      "userData",
                      "newsletterSubscribed",
                    ].forEach((k) => localStorage.removeItem(k));
                    navigate("/");
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                    padding: "14px 20px",
                    background: "transparent",
                    border: "none",
                    borderBottom: "1px solid #f5f5f5",
                    borderTop: "1px solid #f5f5f5",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#dc3545",
                    textAlign: "left",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#dc3545",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#dc3545"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                  </span>
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="pro-acc-main">
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: "12px 16px",
                padding: "8px 12px",
                background: "#f5f5f5",
                color: "#333",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                zIndex: 100,
                position: "relative",
              }}
            >
              <FaBars size={18} />
            </button>
          )}
          <div className="pro-acc-content">
            {sections.map(
              (section) =>
                activeSection === section.id && (
                  <div key={section.id} className="pro-acc-section">
                    {section.content}
                  </div>
                ),
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="custom-modal-overlay"
          onClick={closeModal}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            className="custom-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#fff",
              borderRadius: "20px",
              width: "90%",
              maxWidth: "700px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
              position: "relative",
              margin: "10px",
            }}
          >
            <div
              className="custom-modal-header"
              style={{ padding: "25px 30px", borderBottom: "none" }}
            >
              <h5
                style={{
                  color: "#000",
                  fontSize: "1.4rem",
                  fontWeight: "600",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <span
                  style={{
                    backgroundColor: "#f4f4f4",
                    padding: "10px",
                    borderRadius: "12px",
                    display: "inline-flex",
                  }}
                >
                  {modalType === "edit-profile" && (
                    <FaUser size={18} color="#555" />
                  )}
                  {modalType === "upload-photo" && (
                    <FaCamera size={18} color="#555" />
                  )}
                  {modalType === "subscribe-newsletter" && (
                    <FaEnvelope size={18} color="#555" />
                  )}
                  {modalType === "add-address" && (
                    <FaMapMarkerAlt size={18} color="#555" />
                  )}
                  {modalType === "edit-address" && (
                    <FaPencilAlt size={18} color="#555" />
                  )}
                </span>
                {modalType === "edit-profile" && "Edit Profile"}
                {modalType === "upload-photo" && "Upload Profile Picture"}
                {modalType === "subscribe-newsletter" &&
                  "Subscribe to Newsletter"}
                {modalType === "add-address" && "Add New Address"}
                {modalType === "edit-address" && "Edit Address"}
              </h5>
              <button
                onClick={closeModal}
                style={{
                  position: "absolute",
                  top: "25px",
                  right: "30px",
                  background: "#f0f0f0",
                  border: "none",
                  color: "#444",
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <FaTimes size={14} />
              </button>
            </div>

            <div
              className="custom-modal-body"
              style={{
                padding: "20px",
                maxHeight: "calc(90vh - 160px)",
                overflowY: "auto",
                backgroundColor: "#fafafa",
              }}
            >
              {" "}
              <style>{`
                .modern-form-control { width:100%; padding:14px 16px; border:2px solid #e0e0e0; border-radius:12px; font-size:15px; transition:all 0.3s ease; background:white; box-sizing:border-box; }
                .modern-form-control:focus { border-color:#667eea; box-shadow:0 0 0 4px rgba(102,126,234,0.1); outline:none; }
                .modern-form-label { font-weight:600; color:#333; margin-bottom:8px; font-size:14px; display:block; }
                .modern-select { appearance:none; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 16px center; padding-right:40px; }
              `}</style>
              {getModalForm()}
            </div>
            <div
              style={{
                padding: "16px 20px",
                borderTop: "1px solid #eaeaea",
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                backgroundColor: "#fff",
                flexWrap: "wrap",
                position: "sticky",
                bottom: 0,
                zIndex: 10,
              }}
            >
              <button
                onClick={closeModal}
                style={{
                  padding: "10px 20px",
                  borderRadius: "12px",
                  border: "2px solid #e0e0e0",
                  backgroundColor: "transparent",
                  color: "#666",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  whiteSpace: "nowrap",
                  flex: "1 1 auto",
                  justifyContent: "center",
                }}
              >
                <FaTimes size={12} /> Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={newsletterLoading}
                style={{
                  padding: "10px 20px",
                  borderRadius: "12px",
                  border: "none",
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  background: "#2929b7",
                  alignItems: "center",
                  gap: "6px",
                  whiteSpace: "nowrap",
                  opacity: newsletterLoading ? 0.7 : 1,
                  flex: "1 1 auto",
                  justifyContent: "center",
                }}
              >
                {newsletterLoading ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <FaCheck size={12} />
                )}
                {modalType === "subscribe-newsletter"
                  ? "Confirm Subscribe"
                  : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .pro-order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
}

.pro-order-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.payment-status-badge {
  transition: all 0.2s ease;
}

.payment-status-badge:hover {
  transform: scale(1.02);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pro-order-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .payment-status-badge {
    align-self: flex-start;
  }
}

@media (max-width: 480px) {
  .custom-modal {
    width: 95% !important;
    margin: 10px;
  }
  
  .custom-modal-footer {
    flex-direction: column;
  }
  
  .custom-modal-footer button {
    width: 100%;
    justify-content: center;
  }
}
      `}</style>
      <style>{`
  /* Existing styles ... */
  
  /* Order Image Styles - Fix overlapping */
  .order-image-container {
    position: relative;
    width: 100%;
    max-width: 120px;
    margin: 0 auto 15px auto;
    overflow: hidden;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  .pro-order-img {
    width: 100%;
    height: auto;
    
    object-fit: cover;
    display: block;
  }
  
  /* Make sure breadcrumb section doesn't overflow */
  .breadcrumb-order {
    position: relative;
    overflow: visible;
  }
  
  .order-box {
    position: relative;
    z-index: 1;
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }
  
  /* Ensure proper spacing in order box */
  .order-box .order-image {
    margin-bottom: 15px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  /* If you want a smaller image for mobile */
  @media (max-width: 768px) {
    .order-image-container {
      max-width: 80px;
    }
    
    .pro-order-img {
      max-height: 80px;
    }
  }
`}</style>
    </div>
  );
};

export default Account;
